import { onUnmounted, ref, watch, type Ref } from 'vue'

/**
 * Comportement commun à toutes les surfaces flottantes : modale, tiroir,
 * confirmation.
 *
 * Ces quatre comportements sont ceux qu'on oublie en réécrivant une modale à la
 * main, et c'est exactement pourquoi ils vivent ici une seule fois :
 *
 *   1. **Échap ferme** — la surface du DESSUS, et elle seule ;
 *   2. **le clic sur le fond ferme, mais pas le clic qui a COMMENCÉ dans la
 *      boîte** — une sélection de texte qui glisse hors du cadre ne doit rien
 *      fermer. C'est le détail que personne ne code et que tout le monde subit ;
 *   3. **le défilement de l'arrière-plan est bloqué** tant qu'AU MOINS une
 *      surface est ouverte ;
 *   4. **le focus entre dans la boîte à l'ouverture et REVIENT à son point de
 *      départ à la fermeture**. Sans le retour, un utilisateur au clavier se
 *      retrouve en haut du document après chaque fermeture.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * LE REGISTRE, ET POURQUOI IL EXISTE
 *
 * Une première version gérait chaque surface isolément : `document.body.style
 * .overflow = 'hidden'` à l'ouverture, `= ''` à la fermeture. Quatre défauts
 * réels, tous vérifiés par `__tests__/useOverlay.spec.ts` :
 *
 *   * monter une surface FERMÉE (une modale dans le contenu d'un tiroir ouvert)
 *     débloquait le défilement de la page — le `watch` immédiat écrivait `''` ;
 *   * fermer la surface du dessus débloquait la page alors que celle du dessous
 *     restait ouverte ;
 *   * démonter la surface du dessus faisait la même chose ;
 *   * Échap fermait LES DEUX surfaces d'un coup. Le `stopPropagation()` censé
 *     l'empêcher n'y changeait rien : deux écouteurs posés sur le MÊME nœud
 *     (`document`) sont tous appelés — seul `stopImmediatePropagation` les
 *     départage, et il dépendrait alors de l'ordre d'enregistrement.
 *
 * D'où ce registre au niveau du module : la pile des surfaces ouvertes. Le
 * blocage du défilement se DÉDUIT de la pile (non vide ⇒ bloqué), et Échap ne
 * s'applique qu'à la dernière entrée. Aucun compteur à incrémenter et
 * décrémenter à la main — donc aucun risque de le décrémenter deux fois.
 * ─────────────────────────────────────────────────────────────────────────────
 */

interface EntreeOuverte {
  /** Identité de l'instance. Un symbole : jamais deux fois la même. */
  id: symbol
  /** Cette surface participe-t-elle au blocage du défilement ? */
  bloqueDefilement: boolean
}

/** Pile des surfaces ouvertes, de la plus ancienne à la plus récente. */
const pile: EntreeOuverte[] = []

/**
 * Aligne le défilement du corps sur l'état de la pile.
 *
 * Recalculé depuis la pile à chaque changement, plutôt qu'incrémenté : un état
 * dérivé ne peut pas désynchroniser, là où un compteur finit toujours par
 * rester coincé à 1 après un chemin d'erreur.
 */
function synchroniseDefilement(): void {
  const bloque = pile.some((e) => e.bloqueDefilement)
  document.body.style.overflow = bloque ? 'hidden' : ''
}

function empile(entree: EntreeOuverte): void {
  if (!pile.some((e) => e.id === entree.id)) pile.push(entree)
  synchroniseDefilement()
}

function depile(id: symbol): void {
  const index = pile.findIndex((e) => e.id === id)
  if (index !== -1) pile.splice(index, 1)
  synchroniseDefilement()
}

/** Vrai si cette instance est la surface du dessus. */
function estAuDessus(id: symbol): boolean {
  return pile.length > 0 && pile[pile.length - 1]!.id === id
}

export interface OverlayOptions {
  /** État d'ouverture, observé. */
  open: Ref<boolean> | (() => boolean)
  /** Appelé quand l'utilisateur demande la fermeture (Échap, clic sur le fond). */
  onClose: () => void
  /** Bloquer le défilement de l'arrière-plan. Vrai par défaut. */
  lockScroll?: boolean
}

export function useOverlay(options: OverlayOptions) {
  /** Élément de la boîte : à lier au conteneur du contenu (`ref="box"`). */
  const box = ref<HTMLElement | null>(null)

  const id = Symbol('overlay')
  const bloqueDefilement = options.lockScroll !== false

  /** Vrai quand le geste de souris a commencé DANS la boîte (voir point 2). */
  let pressedInside = false
  /** Élément qui avait le focus avant l'ouverture, pour le lui rendre. */
  let previouslyFocused: HTMLElement | null = null

  const isOpen = () =>
    typeof options.open === 'function' ? options.open() : options.open.value

  function onKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Escape') return
    // Seule la surface du DESSUS réagit. Sans ce filtre, un Échap ferme toute
    // la pile — le tiroir disparaît en même temps que la modale qu'il avait
    // ouverte, et l'utilisateur perd son contexte.
    if (!estAuDessus(id)) return
    options.onClose()
  }

  watch(
    () => isOpen(),
    async (open) => {
      if (!open) {
        depile(id)
        previouslyFocused?.focus()
        previouslyFocused = null
        return
      }

      empile({ id, bloqueDefilement })
      previouslyFocused = document.activeElement as HTMLElement | null

      // Attendre la peinture : à l'instant du watch, la boîte n'est pas encore
      // dans le DOM, et `focus()` n'aurait aucun effet.
      await new Promise((resolve) => requestAnimationFrame(resolve))

      const cible = box.value?.querySelector<HTMLElement>(
        'input:not([type="hidden"]), textarea, select, [data-autofocus], button:not([data-overlay-close])',
      )
      // `?? box.value` : une surface sans champ reçoit tout de même le focus,
      // sinon Échap ne fonctionnerait pas tant que l'utilisateur n'a pas cliqué
      // dedans.
      ;(cible ?? box.value)?.focus()
    },
    { immediate: true },
  )

  document.addEventListener('keydown', onKeydown)

  onUnmounted(() => {
    document.removeEventListener('keydown', onKeydown)
    // Retrait du registre plutôt qu'un déblocage inconditionnel : un composant
    // démonté alors qu'il était ouvert (navigation pendant l'affichage) ne doit
    // ni laisser la page bloquée, ni la débloquer si une autre surface est
    // encore ouverte.
    depile(id)
  })

  function onBackdropMousedown(event: MouseEvent): void {
    pressedInside = box.value?.contains(event.target as Node) ?? false
  }

  /**
   * Fermeture sur clic du fond. DEUX conditions, et il faut les deux :
   *
   *   1. le clic a été reçu SUR le fond lui-même, pas sur un descendant. Un
   *      `click` remonte : sans ce contrôle, activer un bouton à l'intérieur de
   *      la boîte ferme la surface. Le cas n'est pas théorique — Entrée ou
   *      Espace sur un bouton focalisé émet un `click` SANS `mousedown`, donc le
   *      garde-fou du point 2 ne l'attrape pas. Résultat : un utilisateur au
   *      clavier ne pouvait pas valider un formulaire en modale, la boîte se
   *      fermait sous ses doigts. (Défaut réel, attrapé par un test.)
   *   2. le geste n'a pas COMMENCÉ dans la boîte — cas de la sélection de texte
   *      qui glisse hors du cadre et se relâche sur le fond.
   */
  function onBackdropClick(event: MouseEvent): void {
    const surLeFond = event.target === event.currentTarget
    if (surLeFond && !pressedInside) options.onClose()
    pressedInside = false
  }

  return { box, onBackdropMousedown, onBackdropClick }
}

/** Réservé aux tests : vide la pile entre deux scénarios. */
export function __resetOverlayStack(): void {
  pile.length = 0
  synchroniseDefilement()
}
